/**
 * Contextual help button — sits in the header. Opens the help guide
 * most relevant to whatever page the user is currently on.
 *
 * Resolution lives in `pages/help/contextualMap.ts`. If no entry matches
 * the current pathname, the button drops the user on the help catalog.
 */

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, Tooltip } from "@mui/material";
import { HelpOutlined as HelpIcon } from "@mui/icons-material";

import { resolveContextualSlug } from "@/pages/help/contextualMap";
import { useAuthStore } from "@/store/authStore";
import { getGuideBySlug } from "@/pages/help/guides";

export const ContextualHelpButton: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);

  // Always pass a role (null when unknown) so only guides this reader can
  // open are offered; otherwise the button opens the catalog.
  const slug = resolveContextualSlug(pathname, role ?? null);
  const guide = slug ? getGuideBySlug(slug) : null;

  // Tooltip wording: name the specific guide if we have one, otherwise
  // fall back to the catalog ("Browse help guides").
  const tooltip = guide
    ? `Help: ${guide.title}`
    : "Browse help guides";

  const onClick = () => {
    if (guide) {
      navigate(`/help/${guide.slug}`);
    } else {
      navigate("/help");
    }
  };

  return (
    <Tooltip title={tooltip} arrow>
      <IconButton
        onClick={onClick}
        color="inherit"
        size="medium"
        sx={{ mr: 1 }}
        aria-label={tooltip}
      >
        <HelpIcon />
      </IconButton>
    </Tooltip>
  );
};

// Keep the export alongside the map export so a quick CONTEXTUAL_HELP audit
// (matched paths, missing patterns) is one import away.
export { CONTEXTUAL_HELP } from "@/pages/help/contextualMap";
